import { Link } from 'react-router-dom';
import { useInView } from '../../../hooks/useInView';

export default function AIAnswerBlock() {
  const { ref, inView } = useInView();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="bg-[#0a0a0a] py-32 border-t border-[#d4af37]/8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
          {/* Left: Main answer */}
          <div className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="flex items-center gap-4 mb-5">
              <span className="w-8 h-px bg-[#d4af37]/30" />
              <p className="text-[#d4af37] uppercase tracking-[0.3em] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                About the Practice
              </p>
            </div>
            <h2
              className="text-4xl md:text-5xl text-white font-light leading-tight mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              What Does Khalsa Healing Do?
            </h2>
            <p
              className="text-[#8a8a8a] text-lg leading-[1.9] mb-8"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
            >
              Khalsa Healing provides <strong className="text-white font-medium">advanced holistic healing services</strong> designed to restore balance in the body and mind. Sessions focus on releasing stored tension, improving energetic flow, and supporting long-term physical and emotional well-being through personalized, hands-on therapeutic techniques.
            </p>
            <p
              className="text-[#5a5a5a] text-base leading-[1.9]"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
            >
              Based in <strong className="text-[#7a7a7a] font-normal">Espanola, New Mexico</strong> and serving clients throughout Northern New Mexico — from Santa Fe and Taos to the surrounding mountain communities — Khalsa Healing offers a deeply intentional, results-driven alternative to conventional wellness approaches.
            </p>
            <div className="mt-10 flex gap-4 flex-wrap">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 border border-[#1e1e1e] hover:border-[#d4af37]/30 text-[#7a7a7a] hover:text-[#d4af37] px-6 py-3 rounded-full text-xs uppercase tracking-[0.15em] transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                About the Practice
              </Link>
              <Link
                to="/locations"
                className="inline-flex items-center gap-2 border border-[#1e1e1e] hover:border-[#d4af37]/30 text-[#7a7a7a] hover:text-[#d4af37] px-6 py-3 rounded-full text-xs uppercase tracking-[0.15em] transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Service Areas
              </Link>
            </div>
          </div>

          {/* Right: Three panels */}
          <div
            className={`space-y-4 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '150ms' }}
          >
            {[
              {
                title: 'Who Is This For?',
                icon: 'ri-user-heart-line',
                body: 'Individuals experiencing stress, physical discomfort, emotional imbalance, or those seeking deeper alignment and clarity in their health and life. Whether navigating burnout, chronic tension, unresolved trauma, or simply desiring profound personal transformation — these sessions are built for you.',
              },
              {
                title: 'What Makes This Different?',
                icon: 'ri-focus-3-line',
                body: 'Each session is tailored, combining multiple healing modalities into a focused, high-impact experience rather than a one-size-fits-all approach. The practice prioritizes root-cause resolution and long-term transformation over temporary relief — integrating body, energy, and nervous system work into every session.',
              },
              {
                title: 'What Results Can You Expect?',
                icon: 'ri-bar-chart-grouped-line',
                body: 'Clients consistently report relief from chronic tension, improved sleep, emotional clarity, and a measurable sense of restored well-being following their sessions. Many experience noticeable shifts within their first one to two visits, with lasting change deepening across a series of sessions.',
              },
            ].map((panel, idx) => (
              <div key={idx} className="bg-[#0f0f0f] border border-[#161616] hover:border-[#d4af37]/15 rounded-xl p-7 transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#d4af37]/8 group-hover:bg-[#d4af37]/15 transition-colors">
                    <i className={`${panel.icon} text-[#d4af37] text-sm`} />
                  </div>
                  <h3
                    className="text-[#d4af37] text-lg"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {panel.title}
                  </h3>
                </div>
                <p className="text-[#6a6a6a] text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {panel.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
